import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useFetch, Panel, Tag } from '@/components/Common';
import { api, errMsg, fmtTime } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';

export default function CommandBrain() {
  const { data, error, reload } = useFetch('/command/brain/status', [], 15000);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const saveKey = async () => {
    setBusy(true);
    try {
      await api.put('/credentials/openai_command_key', { value: key.trim() });
      setKey(''); reload(); toast.success('OpenAI key saved encrypted. Activation is separate.');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const activate = async enabled => {
    setBusy(true);
    try { await api.put('/command/brain/config', { enabled }); reload(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const result = data?.result;
  const retry = async () => {
    setBusy(true);
    try {
      await api.post('/command/brain/retry');
      reload(); toast.success('One retry queued. The next background cycle runs within a minute.');
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  return <Panel title="NEXUS Command · OpenAI brain" testid="command-openai-brain"
    right={<Tag tone={error ? 'down' : data?.last_success_at ? 'blue' : 'warn'}>
      {error ? 'Status unavailable' : data?.status || 'Reading status'}
    </Tag>}>
    <p className="text-sm text-muted-foreground">OpenAI operational adviser. No monthly application spending cap; your OpenAI billing and quota apply. This app cannot read your credit balance, buy credits or change automatic top-up. It does not guarantee an exact zero-balance cutoff.</p>
    {error && <p className="mt-3 text-sm text-destructive" role="alert">{errMsg(error)}. Any previous review is historical.</p>}
    <div className="mt-4 flex flex-col gap-2 sm:flex-row">
      <Input type="password" autoComplete="off" aria-label="OpenAI API key" placeholder="Paste OpenAI API key here, never in chat"
        value={key} onChange={e => setKey(e.target.value)} disabled={busy}
        data-testid="command-openai-key" />
      <Button disabled={busy || key.trim().length < 4} onClick={saveKey}>Save encrypted key</Button>
    </div>
    <p className="mt-2 text-xs text-muted-foreground">Key: {data?.key_configured ? 'configured — never returned to this screen' : 'not configured'} · Model: {data?.config?.model || 'not loaded'}</p>
    <label className="mt-4 flex items-start gap-3 text-sm">
      <Switch checked={data?.config?.enabled === true} disabled={busy || !!error || !data?.key_configured}
        onCheckedChange={activate} data-testid="command-openai-enabled" />
      <span>Run automatic paid reviews when operational status changes. Send only connection, clock, baseline and engine-health booleans to OpenAI — no account numbers, balances, passwords, raw logs or order authority.</span>
    </label>
    <div className="mt-4 flex flex-wrap gap-2"><Tag>No automatic top-up</Tag><Tag>No order tools</Tag><Tag>No shell tools</Tag><Tag>No automatic code writes</Tag></div>
    {data && <p className="mt-3 font-mono text-xs text-muted-foreground">Completed reviews {data.completed_reviews} · Input tokens {data.input_tokens} · Output tokens {data.output_tokens} · Last successful review {fmtTime(data.last_success_at)}</p>}
    {data?.retry_available && <div className="mt-3 flex flex-wrap items-center gap-3">
      <Button onClick={retry} disabled={busy || !!error || !data?.config?.enabled} variant="outline">Retry paid review now</Button>
      <span className="text-xs text-muted-foreground">After fixing billing or credentials, queue one retry. No new permissions or orders.</span>
    </div>}
    {data?.status === 'retry_requested' && <p className="mt-3 text-sm">One retry queued; waiting for the next background cycle.</p>}
    {data?.next_attempt_at_utc && data?.retry_available ? <p className="mt-2 text-xs text-muted-foreground">Automatic retry no earlier than {fmtTime(new Date(data.next_attempt_at_utc * 1000).toISOString())}.</p> : null}
    <p className="mt-2 text-xs text-muted-foreground">Unchanged status is cached. Failed requests back off. Turning this adviser off does not stop the trading engine or manage existing positions. <Link to="/risk" className="text-primary underline">Trading controls</Link></p>
    {result?.review && <div className="mt-5 space-y-4 rounded border border-border p-4">
      <p className="text-xs text-muted-foreground">Model-generated proposal, not a verified repair. No action executed.</p>
      <p className="text-sm">{result.review.summary}</p>
      {[['hypotheses', 'Likely causes'], ['verification_steps', 'Verification'], ['proposed_repairs', 'Proposed repairs']].map(([field, title]) =>
        <div key={field}><h3 className="text-sm font-medium">{title}</h3><ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          {result.review[field].map((text, i) => <li key={i}>{text}</li>)}
        </ul></div>)}
    </div>}
  </Panel>;
}
